import { formatNumber } from "@/v2-common/utils/utils.formatting"
import Link from "@/v2-backoffice-client/components/link"
import {
  createColumns,
  type ColumnMeta,
  type ColumnMetaMap,
} from "@/v2-backoffice-client/components/table"
import UsageChurnRiskBadge from "@/v2-backoffice-client/usage/scope/UsageChurnRiskBadge"
import UsageTrendBadge from "@/v2-backoffice-client/usage/UsageTrendBadge"
import { formatCompactAmount } from "@/v2-backoffice-client/customer/billing/customerBilling.utils"
import {
  formatTrendDetail,
  SCORE_TOOLTIP,
  TREND_TOOLTIP,
} from "@/v2-backoffice-client/usage/usage.constants"
import {
  buildCustomerActivityLink,
  type PortfolioCustomerSummary,
} from "@/v2-backoffice-client/portfolio/portfolio.utils"

/** A row of any customer table: the account, plus whatever the list adds. */
export type PortfolioCustomerRow = {
  customer: PortfolioCustomerSummary
}

/** Churn levels in severity order, so the column sorts worst first. */
const CHURN_SORT_RANK: Record<
  PortfolioCustomerSummary["summary"]["churnRisk"]["level"],
  number
> = { high: 4, medium: 3, low: 2, new: 1, none: 0 }

/**
 * The columns every customer table shares, as column metas for
 * `createColumns`: the account (a link into its Activity tab), its churn
 * read, the money, the seats, the engagement score and the trend. A list
 * composes the ones it needs with its own columns in between.
 */
export function customerColumns<TRow extends PortfolioCustomerRow>() {
  const name: ColumnMeta<TRow> = {
    id: "customer",
    label: "Customer",
    getValue: (row) => row.customer.name,
    defaultSize: 240,
    renderCell({ row }) {
      return (
        <Link
          to={buildCustomerActivityLink(
            row.original.customer.externalCustomerId,
          )}
          iconSrc={null}
          className="font-medium"
        >
          {row.original.customer.name}
        </Link>
      )
    },
  }
  const churn: ColumnMeta<TRow> = {
    id: "churn",
    label: "Churn risk",
    description:
      "The rule-based churn read of the last 28 days; hover the badge for the signals behind it.",
    getValue: (row) => CHURN_SORT_RANK[row.customer.summary.churnRisk.level],
    defaultSize: 140,
    renderCell({ row }) {
      return (
        <UsageChurnRiskBadge
          level={row.original.customer.summary.churnRisk.level}
          reasons={row.original.customer.summary.churnRisk.reasons}
        />
      )
    },
  }
  const billed: ColumnMeta<TRow> = {
    id: "billed12M",
    label: "Billed 12 months",
    description: "Invoiced over the last 12 months (invoice mirror).",
    getValue: (row) => row.customer.billed12M ?? -1,
    defaultSize: 130,
    className: "tabular-nums",
    renderCell({ row }) {
      const { billed12M, primaryCurrency } = row.original.customer
      return billed12M != null && billed12M > 0
        ? formatCompactAmount(billed12M, primaryCurrency)
        : "—"
    },
  }
  const seats: ColumnMeta<TRow> = {
    id: "seats",
    label: "Seats active",
    description:
      "Seats with tracked activity in the last 28 days, of all seats.",
    getValue: (row) => row.customer.summary.activeUsers28d,
    defaultSize: 110,
    className: "tabular-nums",
    renderCell({ row }) {
      const { activeUsers28d, seatCount } = row.original.customer.summary
      return `${formatNumber(activeUsers28d)} / ${formatNumber(seatCount)}`
    },
  }
  const score: ColumnMeta<TRow> = {
    id: "score",
    label: "Engagement",
    description: SCORE_TOOLTIP,
    getValue: (row) => row.customer.summary.avgScore ?? -1,
    defaultSize: 110,
    className: "tabular-nums",
    renderCell({ row }) {
      const { avgScore } = row.original.customer.summary
      return avgScore != null ? Math.round(avgScore) : "—"
    },
  }
  const trend: ColumnMeta<TRow> = {
    id: "trend",
    label: "Trend 28 d",
    description: TREND_TOOLTIP,
    getValue: (row) =>
      row.customer.summary.trendPct ?? Number.NEGATIVE_INFINITY,
    defaultSize: 110,
    renderCell({ row }) {
      const { summary } = row.original.customer
      return (
        <UsageTrendBadge
          trendPct={summary.trendPct}
          detail={formatTrendDetail(
            summary.weightedEvents28d ?? summary.events28d,
            summary.weightedEventsPrev28d,
          )}
        />
      )
    },
  }
  return { name, churn, billed, seats, score, trend }
}

/**
 * The column defs a customer list dialog mounts: sortable, nothing hidable
 * (the lists are short and every column earns its place). Call it once at
 * module scope, next to the column metas it is built from.
 */
export function createCustomerColumns<TRow extends PortfolioCustomerRow>(
  columns: ColumnMetaMap<TRow>,
) {
  return createColumns<TRow>({
    enableSorting: true,
    enableHiding: false,
    columns,
  })
}
